//selectmodal.js
import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Button,
  Select,
  Breadcrumb,
  Modal,
  message,
  Input,
  Tag,
  Alert,
  Progress,
  Tooltip,
  Tour,
} from "antd";
import { useNavigate } from "react-router-dom";
import {
  RocketOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  LockOutlined,
  DashboardOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  ThunderboltOutlined,
  SettingOutlined,
  ReloadOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import axios from "../../utils/axios";
import Header from "../header/header2";
import { modeMap } from "../../constants/models";

const { Option } = Select;
const MAX_DASHBOARD_ATTEMPTS = 2;

const SelectModelPage = () => {
  const navigate = useNavigate();
  const citriot = JSON.parse(localStorage.getItem("citriot")) || {};
  const user = JSON.parse(localStorage.getItem("user")) || {};
  const projectname = localStorage.getItem("projectname");
  const organizationId = localStorage.getItem("organizationId");

  const [loading, setLoading] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [pretrainedModels, setPretrainedModels] = useState([]);
  const [customModels, setCustomModels] = useState([]);
  const [cameraModelMap, setCameraModelMap] = useState({});
  const [useCases, setUseCases] = useState([]);
  const [applyToAllModels, setApplyToAllModels] = useState([]);
  const [pipelineStatus, setPipelineStatus] = useState({});
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [viewPipelineModal, setViewPipelineModal] = useState(false);
  const [viewPipelineData, setViewPipelineData] = useState(null);
  const [cameraPipelineMap, setCameraPipelineMap] = useState({});
  const [modelConfig, setModelConfig] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showAllPretrained, setShowAllPretrained] = useState(false);
  const [modelSearch, setModelSearch] = useState("");
  const [dashboardAttempts, setDashboardAttempts] = useState(0);
  const [dashboardStatus, setDashboardStatus] = useState(null);
  const [globalPipeline, setGlobalPipeline] = useState(null);

  // ── Tour state ──
  const [tourOpen, setTourOpen] = useState(false);

  // ── Tour refs ──
  const refDashboardCard = useRef(null);
  const refUseCasesCard = useRef(null);
  const refPretrainedSection = useRef(null);
  const refCustomSection = useRef(null);
  const refCameraAssignment = useRef(null);
  const refApplyToAll = useRef(null);
  const refSaveBtn = useRef(null);
  const refTourBtn = useRef(null);

  const remainingAttempts = MAX_DASHBOARD_ATTEMPTS - dashboardAttempts;
  const canBuildDashboard = modelConfig && !hasUnsavedChanges;
  const isGlobalPipelineCreated = !!globalPipeline;
  const allPipelinesAvailable =
    cameras.length > 0 && cameras.every((cam) => pipelineStatus[cam.channel]);
  const isGlobalPipelineCreatedFromStatus = allPipelinesAvailable;

  const allAvailablePretrained = Object.keys(modeMap).map((name) => ({
    name,
    type: "pretrained",
  }));

  const remainingPretrained = allAvailablePretrained.filter(
    (m) => !pretrainedModels.some((p) => p.name === m.name)
  );

  const handleAddPretrainedModel = (model) => {
    setPretrainedModels((prev) => [...prev, model]);
  };

  const fetchPipelineStatus = async () => {
    const res = await axios.post("/get-dataflow-status", {
      projectname,
      organizationId,
    });
    if (res?.data?.pipelineMap) {
      setPipelineStatus(res.data.pipelineMap || {});
    }
  };

  const fetchCameraPipelineAndSetModels = async (cam) => {
    try {
      const res = await axios.post("/get-camera-pipeline", {
        projectname,
        organizationId,
        cameraId: cam.channel,
      });
      if (res?.data?.pipeline) {
        setCameraPipelineMap((prev) => ({
          ...prev,
          [cam.channel]: res.data.pipeline,
        }));
      }
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleViewPipeline = (cam) => {
    const pipeline = cameraPipelineMap[cam.channel];
    if (!pipeline) {
      message.warning("Pipeline data not available");
      return;
    }
    setViewPipelineData({ cameraName: cam.cameraName, channel: cam.channel, ...pipeline });
    setViewPipelineModal(true);
  };

  useEffect(() => {
    cameras.forEach((cam) => {
      if (pipelineStatus[cam.channel]) fetchCameraPipelineAndSetModels(cam);
    });
  }, [cameras, pipelineStatus]);

  useEffect(() => {
    setHasUnsavedChanges(true);
  }, [pipelineStatus]);

  const handleApiError = (err) => {
    if (err.response?.data?.msg) message.warning(err.response.data.msg);
    else if (err.response?.data?.userName) message.warning(err.response.data.userName[0]);
    else if (err.response?.status === 401) message.error("User not authorised");
    else message.error("Something went wrong");
  };

  const fetchCameras = async () => {
    try {
      const res = await axios.post("/fetch-cameras", { organizationId, projectname });
      if (res.data.success) setCameras(res.data.cameras || []);
    } catch (err) { handleApiError(err); }
  };

  const fetchUseCases = async () => {
    try {
      const res = await axios.post("/get-usecases", { projectname });
      if (res?.data?.functionalities) setUseCases(res.data.functionalities);
    } catch (err) { handleApiError(err); }
  };

  const fetchTrainedModels = async () => {
    try {
      const res = await axios.post("/fetch-trained-models", { projectname, organizationId });
      if (res?.data?.models?.length > 0) {
        setCustomModels(res.data.models.map((m) => ({ name: m.useCase, type: "custom" })));
      }
    } catch (err) { handleApiError(err); }
  };

  const fetchCameraModelConfiguration = async () => {
    try {
      const res = await axios.post("/get-camera-model-configuration", { projectname, organizationId });
      if (res?.data?.config?.cameras) {
        const map = {};
        res.data.config.cameras.forEach((cam) => { map[cam.channel] = cam.models; });
        setCameraModelMap(map);
        setModelConfig(res.data.config);
        setHasUnsavedChanges(false);
      }
    } catch (err) { handleApiError(err); }
  };

  const fetchDashboardStatus = async () => {
    try {
      const res = await axios.post("/get-project-details", { projectName: projectname, organizationId });
      if (res?.data?.status) {
        const status = res.data.status;
        setDashboardStatus(status.dashboardStatus);
        if (status.dashboardStatus === "ALLOW_REBUILD") setDashboardAttempts(1);
        else if (status.dashboardStatus === "BLOCK_REBUILD_LIMIT") setDashboardAttempts(2);
        else setDashboardAttempts(0);
      }
    } catch (_) {}
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchCameras(), fetchUseCases(), fetchTrainedModels(),
      fetchPipelineStatus(), fetchCameraModelConfiguration(), fetchDashboardStatus(),
    ]).finally(() => setLoading(false));
  }, []);

  const handleApplyToAll = (selectedValues) => {
    setApplyToAllModels(selectedValues);
    setHasUnsavedChanges(true);
    const models = selectedValues.map((val) => {
      const [name, modelType] = val.split("__");
      return { name, modelType };
    });
    const updatedMap = {};
    cameras.forEach((cam) => { updatedMap[cam.channel] = models; });
    setCameraModelMap(updatedMap);
  };

  const handleFinalSave = async () => {
    try {
      const camerasPayload = cameras.map((cam) => ({
        cameraId: cam._id,
        channel: cam.channel,
        cameraName: cam.cameraName,
        models: cameraModelMap[cam.channel] || [],
        pipeline: cameraPipelineMap[cam.channel]?.pipeline || [],
      }));
      await axios.post("/save-model-configuration", {
        projectname, organizationId, cameras: camerasPayload, username: user.username,
      });
      message.success("Configuration saved successfully");
      setConfirmVisible(false);
      setHasUnsavedChanges(false);
      fetchCameraModelConfiguration();
      fetchDashboardStatus();
    } catch (err) { handleApiError(err); }
  };

  const handleRemovePretrainedModel = (modelName) => {
    setPretrainedModels((prev) => prev.filter((m) => m.name !== modelName));
    setCameraModelMap((prev) => {
      const updated = {};
      Object.keys(prev).forEach((channel) => {
        updated[channel] = prev[channel].filter((m) => m.name !== modelName);
      });
      return updated;
    });
    setApplyToAllModels((prev) => prev.filter((v) => !v.startsWith(`${modelName}__`)));
    setHasUnsavedChanges(true);
  };

  useEffect(() => {
    const initial = useCases
      .filter((uc) => modeMap[uc])
      .map((uc) => ({ name: uc, type: "pretrained" }));
    setPretrainedModels(initial);
  }, [useCases]);

  const allPipelinesSaved =
    cameras.length > 0 && cameras.every((cam) => pipelineStatus[cam.channel]);

  const handleGoToDashboard = () => {
    if (!modelConfig) {
      message.warning("Please save your model configuration before building the dashboard.");
      return;
    }
    if (hasUnsavedChanges) {
      message.warning("You have unsaved changes. Please save your configuration first.");
      return;
    }
    navigate("/dashboard-builder");
  };

  const getDashboardButtonLabel = () => {
    if (dashboardStatus === "BLOCK_REBUILD_LIMIT") return "Dashboard Locked";
    if (dashboardStatus === "ALLOW_REBUILD") return "Rebuild Dashboard";
    return "Build Dashboard";
  };

  const isDashboardLocked = dashboardStatus === "BLOCK_REBUILD_LIMIT";

  // ── Tour Steps ──
  const tourSteps = [
    {
      title: (
        <span className="flex items-center gap-2">
          <RocketOutlined className="text-blue-500" />
          Welcome to Model Configuration
        </span>
      ),
      description: (
        <div className="space-y-2">
          <p>This page lets you configure AI models for your project cameras.</p>
          <p className="text-gray-500 text-xs">
            Follow this tour to understand how to assign models, create pipelines, and build your dashboard.
          </p>
        </div>
      ),
      target: null, // centered welcome step
    },
    {
      title: (
        <span className="flex items-center gap-2">
          <DashboardOutlined className="text-blue-500" />
          Dashboard Builder
        </span>
      ),
      description: (
        <p>
          Once you've saved your configuration, use this card to build your analytics dashboard.
          You have <strong>{MAX_DASHBOARD_ATTEMPTS} build attempts</strong> — use them wisely.
          The button unlocks only after saving.
        </p>
      ),
      target: () => refDashboardCard.current,
      placement: "bottom",
    },
    {
      title: (
        <span className="flex items-center gap-2">
          <CheckCircleOutlined className="text-green-500" />
          Project Use Cases
        </span>
      ),
      description: (
        <p>
          These are the AI use cases defined for your project. A <Tag color="blue" style={{margin:0}}>Pre-trained Ready</Tag> tag means a model
          is ready to use. <Tag color="red" style={{margin:0}}>Training Required</Tag> means you need to train a custom model first.
        </p>
      ),
      target: () => refUseCasesCard.current,
      placement: "bottom",
    },
    {
      title: (
        <span className="flex items-center gap-2">
          <ThunderboltOutlined className="text-blue-500" />
          Pre-trained Models
        </span>
      ),
      description: (
        <p>
          These models come ready-to-use with no training needed. You can add more from the library
          using <strong>"View More"</strong>, or remove ones you don't need by clicking <strong>✕</strong>.
        </p>
      ),
      target: () => refPretrainedSection.current,
      placement: "top",
    },
    {
      title: (
        <span className="flex items-center gap-2">
          <PlusOutlined className="text-purple-500" />
          Custom Trained Models
        </span>
      ),
      description: (
        <p>
          Need a model for a unique use case? Click <strong>"Train Custom Model"</strong> to start
          training your own AI model tailored to your data. Custom models appear here once trained.
        </p>
      ),
      target: () => refCustomSection.current,
      placement: "top",
    },
    {
      title: (
        <span className="flex items-center gap-2">
          <SettingOutlined className="text-indigo-500" />
          Apply Models to All Cameras
        </span>
      ),
      description: (
        <p>
          Use this dropdown to assign models to <strong>every camera at once</strong>. It's a
          shortcut — you can still customize each camera individually below.
        </p>
      ),
      target: () => refApplyToAll.current,
      placement: "bottom",
    },
    {
      title: (
        <span className="flex items-center gap-2">
          <SettingOutlined className="text-gray-600" />
          Camera Assignment
        </span>
      ),
      description: (
        <p>
          Assign specific models to each camera here. After assigning models, click{" "}
          <strong>"Create Pipeline"</strong> to configure the data processing flow for that camera.
          All pipelines must be created before you can save.
        </p>
      ),
      target: () => refCameraAssignment.current,
      placement: "top",
    },
    {
      title: (
        <span className="flex items-center gap-2">
          <LockOutlined className="text-green-500" />
          Save Configuration
        </span>
      ),
      description: (
        <p>
          Once all cameras have pipelines, click <strong>"Save Configuration"</strong> to lock in
          your setup. This unlocks the <strong>Dashboard Builder</strong> above.
        </p>
      ),
      target: () => refSaveBtn.current,
      placement: "top",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Header auth={citriot?.auth} />

      <div className="max-w-7xl mx-auto px-4 pt-6 pb-12">

        {/* ── Breadcrumb + Buttons ── */}
        <div className="flex items-center justify-between mb-6">
          <Breadcrumb
            items={[
              {
                title: (
                  <button
                    onClick={() => navigate("/create-project")}
                    className="text-blue-600 hover:text-blue-700 font-medium bg-none border-none cursor-pointer p-0"
                  >
                    Select Project
                  </button>
                ),
              },
              {
                title: (
                  <span className="text-gray-600 font-medium">
                    Model Configuration
                  </span>
                ),
              },
            ]}
          />

          <div className="flex items-center gap-2">
            {/* Tour trigger button */}
            <Tooltip title="Take a guided tour of this page">
              <Button
                ref={refTourBtn}
                icon={<QuestionCircleOutlined />}
                onClick={() => setTourOpen(true)}
                style={{ borderRadius: 10 }}
              >
                Page Tour
              </Button>
            </Tooltip>

            <Button
              type="primary"
              icon={<SettingOutlined />}
              onClick={() => navigate("/view-camera")}
              style={{ borderRadius: 10, border: "none" }}
            >
              View Camera
            </Button>
          </div>
        </div>

        {/* ── Page Header ── */}
        <div className="text-center mb-12">
          <RocketOutlined className="text-5xl text-blue-600 mb-3" />
          <h1 className="text-3xl font-bold text-gray-800">Configure Models for</h1>
          <p className="text-lg text-blue-600 font-semibold mt-1">{projectname}</p>
        </div>

        {/* ── Dashboard Builder Card ── */}
        <div ref={refDashboardCard}>
          <Card className="rounded-3xl shadow-lg mb-10 border border-slate-200 overflow-hidden">
            <div
              className={`p-6 rounded-2xl ${
                canBuildDashboard && !isDashboardLocked
                  ? "bg-gradient-to-r from-blue-500 to-indigo-600"
                  : "bg-gradient-to-r from-gray-100 to-slate-100"
              }`}
            >
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-4">
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      canBuildDashboard && !isDashboardLocked
                        ? "bg-white/20 backdrop-blur"
                        : "bg-white border border-gray-200"
                    }`}
                  >
                    <DashboardOutlined
                      className={`text-2xl ${
                        canBuildDashboard && !isDashboardLocked
                          ? "text-white"
                          : isDashboardLocked
                          ? "text-gray-400"
                          : "text-gray-500"
                      }`}
                    />
                  </div>
                  <div>
                    <h3
                      className={`text-lg font-bold ${
                        canBuildDashboard && !isDashboardLocked ? "text-white" : "text-gray-700"
                      }`}
                    >
                      Dashboard Builder
                    </h3>
                    <p
                      className={`text-sm mt-0.5 ${
                        canBuildDashboard && !isDashboardLocked ? "text-blue-100" : "text-gray-500"
                      }`}
                    >
                      {isDashboardLocked
                        ? "You have used all rebuild attempts. Dashboard is locked."
                        : !modelConfig
                        ? "Save your model configuration first to unlock dashboard building."
                        : hasUnsavedChanges
                        ? "You have unsaved changes — save your configuration to proceed."
                        : remainingAttempts === 0
                        ? "No rebuild attempts remaining."
                        : `${remainingAttempts} build attempt${remainingAttempts > 1 ? "s" : ""} remaining.`}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2 min-w-[180px]">
                  {modelConfig && (
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs font-semibold ${
                          canBuildDashboard && !isDashboardLocked ? "text-blue-100" : "text-gray-500"
                        }`}
                      >
                        Attempts:
                      </span>
                      <div className="flex gap-1.5">
                        {Array.from({ length: MAX_DASHBOARD_ATTEMPTS }).map((_, i) => (
                          <div
                            key={i}
                            className={`w-2.5 h-2.5 rounded-full ${
                              i < dashboardAttempts
                                ? canBuildDashboard && !isDashboardLocked
                                  ? "bg-white/30"
                                  : "bg-gray-300"
                                : canBuildDashboard && !isDashboardLocked
                                ? "bg-white"
                                : "bg-blue-400"
                            }`}
                          />
                        ))}
                      </div>
                      <span
                        className={`text-xs font-bold ${
                          canBuildDashboard && !isDashboardLocked
                            ? "text-white"
                            : remainingAttempts === 0
                            ? "text-red-500"
                            : "text-gray-700"
                        }`}
                      >
                        {remainingAttempts}/{MAX_DASHBOARD_ATTEMPTS} left
                      </span>
                    </div>
                  )}

                  <Tooltip
                    title={
                      !user?.subscribe
                        ? "Please subscribe to use this feature"
                        : !modelConfig
                        ? "Save model configuration first to enable dashboard building"
                        : hasUnsavedChanges
                        ? "Save your configuration to proceed"
                        : isDashboardLocked
                        ? "You have used all rebuild attempts"
                        : ""
                    }
                  >
                    <Button
                      size="large"
                      icon={
                        isDashboardLocked ? (
                          <LockOutlined />
                        ) : !modelConfig || hasUnsavedChanges ? (
                          <WarningOutlined />
                        ) : (
                          <DashboardOutlined />
                        )
                      }
                      disabled={!user?.subscribe || isDashboardLocked || !canBuildDashboard}
                      onClick={() => {
                        if (!user?.subscribe) {
                          message.warning("Please subscribe");
                          return;
                        }
                        handleGoToDashboard();
                      }}
                      className={`font-semibold h-10 px-6 rounded-lg transition-all ${
                        !user?.subscribe
                          ? "cursor-not-allowed opacity-70"
                          : canBuildDashboard && !isDashboardLocked
                          ? "bg-white text-blue-600 hover:bg-blue-50 border-0 shadow-md"
                          : "bg-white/10 text-gray-500 border-gray-200"
                      }`}
                    >
                      {getDashboardButtonLabel()}
                    </Button>
                  </Tooltip>
                </div>
              </div>

              {!modelConfig && (
                <div className="mt-4 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                  <InfoCircleOutlined className="text-amber-500 text-base flex-shrink-0" />
                  <p className="text-sm text-amber-700 font-medium">
                    <strong>Action Required:</strong> Complete and save your camera model configuration below before you can build the dashboard.
                  </p>
                </div>
              )}

              {modelConfig && hasUnsavedChanges && (
                <div className="mt-4 flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3">
                  <WarningOutlined className="text-orange-500 text-base flex-shrink-0" />
                  <p className="text-sm text-orange-700 font-medium">
                    <strong>Unsaved Changes:</strong> You have modified the configuration. Save it first before building the dashboard.
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* ── Project Use Cases ── */}
        <div ref={refUseCasesCard}>
          <Card className="rounded-3xl shadow-lg mb-10 bg-gradient-to-br from-white to-slate-50 border border-slate-200">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-lg font-semibold text-gray-800">Project Required Use Cases</h2>
              {!modelConfig && (
                <Button size="small" type="link" onClick={() => navigate("/home")} className="font-medium">
                  Edit Use Cases
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {useCases.map((uc) => {
                const hasPretrained = !!modeMap[uc];
                const hasCustom = customModels.some((c) => c.name === uc);
                let tag, bg;
                if (hasCustom) {
                  tag = <Tag color="purple">Custom Ready</Tag>;
                  bg = "bg-purple-50 border-purple-200";
                } else if (hasPretrained) {
                  tag = <Tag color="blue">Pre-trained Ready</Tag>;
                  bg = "bg-blue-50 border-blue-200";
                } else {
                  tag = <Tag color="red">Training Required</Tag>;
                  bg = "bg-red-50 border-red-200";
                }
                return (
                  <div key={uc} className={`rounded-xl border p-4 flex justify-between items-center ${bg}`}>
                    <span className="font-medium text-gray-800">{uc}</span>
                    {tag}
                  </div>
                );
              })}
            </div>

            <div className="mt-4 text-xs text-gray-600 leading-relaxed">
              <b>Training Required</b> means no suitable model exists yet. You must train a custom model before locking configuration.
            </div>
          </Card>
        </div>

        {/* ── Available Models ── */}
        <Card className="rounded-3xl shadow-lg mb-10 border border-slate-200">

          {/* Pretrained */}
          <div ref={refPretrainedSection}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                Pre-trained Models
              </h3>
              {remainingPretrained.length > 0 && (
                <Button type="link" onClick={() => setShowAllPretrained(!showAllPretrained)}>
                  {showAllPretrained ? "Hide" : "View More"}
                </Button>
              )}
            </div>

            {user?.subscribe === false && (
              <div className="flex justify-between items-center mb-4 mt-3">
                <Button type="primary" onClick={() => navigate("/sidebar")}>
                  Dataflow Test
                </Button>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
              {pretrainedModels.length ? (
                pretrainedModels.map((m) => (
                  <Card key={m.name} size="small" className="hover:shadow-md transition-all border rounded-xl">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">{m.name}</span>
                      <div className="flex items-center gap-2">
                        <Tag color="blue">Pre-trained</Tag>
                        <Button size="small" type="text" danger onClick={() => handleRemovePretrainedModel(m.name)}>✕</Button>
                      </div>
                    </div>
                  </Card>
                ))
              ) : (
                <div className="text-sm text-gray-500">No pre-trained models available.</div>
              )}
            </div>
          </div>

          {/* Custom */}
          <div ref={refCustomSection}>
            <div className="flex justify-between items-center mb-4 mt-3">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                Custom Trained Models
              </h3>
              <Tooltip title={user?.subscribe ? "" : "Please subscribe to use this feature"}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    if (!user?.subscribe) { message.warning("Please subscribe"); return; }
                    navigate("/sidebar");
                  }}
                  disabled={!user?.subscribe}
                  className={!user?.subscribe ? "cursor-not-allowed opacity-70" : ""}
                >
                  Train Custom Model
                </Button>
              </Tooltip>
            </div>
          </div>
        </Card>

        {/* ── Camera Assignment ── */}
        <div ref={refCameraAssignment}>
          <Card
            title={
              <div className="flex justify-between items-center">
                <span>Assign Models to Cameras</span>
              </div>
            }
            className="rounded-3xl shadow-lg border border-slate-200"
          >
            {/* Apply to All */}
            <div ref={refApplyToAll} className="mb-6 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl">
              <div className="flex justify-between items-center mb-2">
                <div className="font-semibold text-gray-800">Apply Models to All Cameras</div>
                <Tag color="blue">Optional</Tag>
              </div>
              <Select
                mode="multiple"
                className="w-full"
                size="large"
                placeholder="Select models to apply to all cameras"
                value={applyToAllModels}
                onChange={handleApplyToAll}
                allowClear
              >
                {pretrainedModels.map((m) => (
                  <Option key={`pretrained-${m.name}`} value={`${m.name}__pretrained`}>
                    <div className="flex justify-between items-center">
                      <span>{m.name}</span>
                      <Tag color="blue">Pre-trained</Tag>
                    </div>
                  </Option>
                ))}
                {customModels.map((m) => (
                  <Option key={`custom-${m.name}`} value={`${m.name}__custom`}>
                    <div className="flex justify-between items-center">
                      <span>{m.name}</span>
                      <Tag color="purple">Custom</Tag>
                    </div>
                  </Option>
                ))}
              </Select>
              <p className="mt-2 text-xs text-gray-600">
                Selecting models here will automatically assign them to all cameras. You can still customize individual cameras below.
              </p>
            </div>

            <div className="flex gap-3 mb-4">
              <Button
                type={!isGlobalPipelineCreatedFromStatus ? "primary" : "default"}
                disabled={cameras.length === 0 || !cameras.every((cam) => cameraModelMap[cam.channel]?.length)}
                onClick={() => navigate("/dataFlowPre", { state: { isGlobal: true } })}
              >
                Create Pipeline for All Cameras
              </Button>
              <Button
                disabled={!isGlobalPipelineCreatedFromStatus}
                onClick={() => navigate("/dataFlowPre", { state: { isGlobal: true, edit: true } })}
              >
                Edit Pipeline for All Cameras
              </Button>
            </div>

            {cameras.map((cam) => (
              <div key={cam._id} className="mb-6 p-5 bg-white border border-slate-200 rounded-2xl shadow-sm">
                <div className="flex justify-between items-center mb-3">
                  <div className="font-semibold text-gray-800">
                    {cam.cameraName}
                    <span className="ml-2 text-xs text-gray-500">Channel {cam.channel}</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="small"
                      type={pipelineStatus[cam.channel] ? "default" : "primary"}
                      disabled={!(cameraModelMap[cam.channel]?.length)}
                      onClick={() =>
                        navigate("/dataFlowPre", {
                          state: { cameraId: cam.channel, cameraName: cam.cameraName, models: cameraModelMap[cam.channel] },
                        })
                      }
                    >
                      {pipelineStatus[cam.channel] ? "Edit Pipeline" : "Create Pipeline"}
                    </Button>
                    {pipelineStatus[cam.channel] && (
                      <Button size="small" icon={<CheckCircleOutlined />} onClick={() => handleViewPipeline(cam)}>
                        View Pipeline
                      </Button>
                    )}
                  </div>
                </div>

                <Select
                  mode="multiple"
                  labelInValue
                  className="w-full"
                  placeholder="Select models for this camera"
                  value={(cameraModelMap[cam.channel] || []).map((m) => ({
                    value: `${m.name}__${m.modelType}`,
                    label: (
                      <div className="flex justify-between items-center">
                        <span>{m.name}</span>
                        <Tag color={m.modelType === "pretrained" ? "blue" : "purple"}>
                          {m.modelType === "pretrained" ? "Pre-trained" : "Custom"}
                        </Tag>
                      </div>
                    ),
                  }))}
                  onChange={(selectedItems) => {
                    const models = selectedItems.map((item) => {
                      const [name, modelType] = item.value.split("__");
                      return { name, modelType };
                    });
                    setCameraModelMap((prev) => ({ ...prev, [cam.channel]: models }));
                    setHasUnsavedChanges(true);
                  }}
                >
                  {pretrainedModels.map((m) => (
                    <Option key={`pretrained-${m.name}`} value={`${m.name}__pretrained`}>
                      <div className="flex justify-between items-center">
                        <span>{m.name}</span>
                        <Tag color="blue">Pre-trained</Tag>
                      </div>
                    </Option>
                  ))}
                  {customModels.map((m) => (
                    <Option key={`custom-${m.name}`} value={`${m.name}__custom`}>
                      <div className="flex justify-between items-center">
                        <span>{m.name}</span>
                        <Tag color="purple">Custom</Tag>
                      </div>
                    </Option>
                  ))}
                </Select>
              </div>
            ))}
          </Card>
        </div>

        {/* ── Final Action ── */}
        <div className="mt-6 flex flex-col gap-3">
          {(!modelConfig || hasUnsavedChanges) && (
            <div ref={refSaveBtn}>
              <Tooltip title={!user?.subscribe ? "Please subscribe to use this feature" : ""}>
                <Button
                  type="primary"
                  size="large"
                  block
                  disabled={!user?.subscribe || !allPipelinesSaved}
                  onClick={() => {
                    if (!user?.subscribe) {
                      message.warning("Please subscribe");
                      return;
                    }
                    setConfirmVisible(true);
                  }}
                  className={!user?.subscribe ? "cursor-not-allowed opacity-70" : ""}
                >
                  Save Configuration
                </Button>
              </Tooltip>
            </div>
          )}

          {modelConfig && !hasUnsavedChanges && (
            <Tooltip title={!user?.subscribe ? "Please subscribe to use this feature" : ""}>
              <Button
                type="primary"
                size="large"
                block
                disabled={!user?.subscribe || isDashboardLocked}
                onClick={() => {
                  if (!user?.subscribe) {
                    message.warning("Please subscribe");
                    return;
                  }
                  handleGoToDashboard();
                }}
                icon={<DashboardOutlined />}
                className={`h-12 text-base font-semibold rounded-lg shadow-lg ${
                  !user?.subscribe ? "cursor-not-allowed opacity-70" : ""
                }`}
              >
                {getDashboardButtonLabel()}
                {modelConfig && !isDashboardLocked && (
                  <span className="ml-2 text-xs font-normal opacity-80">
                    ({remainingAttempts} attempt{remainingAttempts !== 1 ? "s" : ""} left)
                  </span>
                )}
              </Button>
            </Tooltip>
          )}
        </div>
      </div>

      {/* ── Save Confirm Modal ── */}
      <Modal
        open={confirmVisible}
        onCancel={() => setConfirmVisible(false)}
        onOk={handleFinalSave}
        okText="Save Configuration"
        title={
          <div className="flex items-center gap-2">
            <LockOutlined className="text-blue-500" />
            <span>Confirm Project Configuration</span>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-gray-800">
            You are about to <b className="text-blue-600">save this project configuration</b>.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm font-semibold text-blue-700 mb-2">ℹ️ Configuration Summary</p>
            <ul className="text-sm text-blue-700 list-disc pl-5 space-y-1">
              <li>Selected use cases will be applied</li>
              <li>Models will be assigned to cameras</li>
              <li>Dataflow / pipeline configuration will be saved</li>
              <li>Camera–model mapping will be updated</li>
            </ul>
          </div>
          <p className="text-sm text-gray-600">You can update or change these preferences later if needed.</p>
        </div>
      </Modal>

      {/* ── View Pipeline Modal ── */}
      <Modal
        open={viewPipelineModal}
        onCancel={() => setViewPipelineModal(false)}
        footer={null}
        title={`Pipeline – ${viewPipelineData?.cameraName} (Channel ${viewPipelineData?.channel})`}
        width={700}
      >
        {viewPipelineData && (
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">Models / Use Cases</h4>
              <div className="flex flex-wrap gap-2">
                {viewPipelineData.usecases?.map((u, i) => (
                  <Tag key={i} color={u.modelType === "pretrained" ? "blue" : "purple"}>
                    {u.name} ({u.modelType})
                  </Tag>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Preprocessing</h4>
              {Object.entries(viewPipelineData.preprocessing || {})
                .filter(([_, v]) => v.enabled)
                .map(([k, v]) => <Tag key={k} color="gold">{v.label}</Tag>)}
            </div>
            <div>
              <h4 className="font-semibold mb-2">Augmentation</h4>
              {Object.entries(viewPipelineData.augmentation || {})
                .filter(([_, v]) => v.enabled)
                .map(([k, v]) => <Tag key={k} color="green">{v.label}</Tag>)}
            </div>
            <div>
              <h4 className="font-semibold mb-2">Pipeline Flow</h4>
              <div className="flex flex-wrap items-center gap-2">
                <Tag color="default">Original</Tag>
                {viewPipelineData.pipeline?.map((step, index) => (
                  <React.Fragment key={index}>
                    <span className="text-gray-400">→</span>
                    <Tag color="blue">{step.type}</Tag>
                  </React.Fragment>
                ))}
                <span className="text-gray-400">→</span>
                <Tag color="purple">Final</Tag>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ── All Pretrained Modal ── */}
      <Modal
        open={showAllPretrained}
        onCancel={() => setShowAllPretrained(false)}
        footer={null}
        width={900}
        title="All Pre-trained Models"
      >
        <Input.Search
          placeholder="Search models (e.g. person, car, phone)"
          allowClear
          className="mb-4"
          value={modelSearch}
          onChange={(e) => setModelSearch(e.target.value)}
        />
        <div className="max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {allAvailablePretrained
              .filter((m) => m.name.toLowerCase().includes(modelSearch.toLowerCase()))
              .map((m) => {
                const isAdded = pretrainedModels.some((p) => p.name === m.name);
                return (
                  <Card key={m.name} size="small" className={isAdded ? "border-green-400" : ""}>
                    <div className="flex justify-between items-center gap-2">
                      <span className="text-sm font-medium">{m.name}</span>
                      {isAdded ? (
                        <Tag color="green">Added</Tag>
                      ) : (
                        <Button size="small" type="primary" onClick={() => handleAddPretrainedModel(m)}>Add</Button>
                      )}
                    </div>
                  </Card>
                );
              })}
          </div>
        </div>
      </Modal>

      {/* ── Ant Design Tour ── */}
      <Tour
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        steps={tourSteps}
        indicatorsRender={(current, total) => (
          <span className="text-xs text-gray-500">
            {current + 1} / {total}
          </span>
        )}
      />
    </div>
  );
};

export default SelectModelPage;